import { ColorsConfig } from '@ddt-web/config';
import { NumberUtil, ObjectUtil, StringUtil } from '@ddt-web/generic-utils';

import { HeatmapData } from '../models/heatmap-data.interface';
import { DefaultChartConfig } from './default-chart.config';
import { ChartType } from './enums/chart-type.enum';

declare const Highcharts;

const DEFAULT_HEATMAP_CONFIG: any = {
	chart: {
		type: 'heatmap',
		marginTop: 50,
		style: {
			fontFamily: 'FortBook, Arial, sans-serif'
		}
	},
	series: [
		{
			name: 'Fund Correlation',
			borderWidth: 1,
			borderColor: '#FFFFFF',
			turboThreshold: 0
		}
	],
	xAxis: {
		opposite: true,
		lineWidth: 1,
		lineColor: '#a5a5a5',
		labels: {
			rotation: 270,
			style: {
				fontSize: '14px'
			}
		}
	},
	yAxis: {
		title: null,
		reversed: true,
		gridLineWidth: 0,
		lineWidth: 1,
		lineColor: '#a5a5a5',
		labels: {
			style: {
				fontSize: '14px'
			}
		}
	},
	colorAxis: {
		dataClasses: [
			{
				from: -1,
				to: -0.2,
				color: ColorsConfig.CORRELATION_MATRIX_CATEGORY_COLOR.NEGATIVE
			},
			{
				from: -0.2,
				to: 0.2,
				color: ColorsConfig.CORRELATION_MATRIX_CATEGORY_COLOR.UNCORRELATED
			},
			{
				from: 0.2,
				to: 0.6,
				color: ColorsConfig.CORRELATION_MATRIX_CATEGORY_COLOR.LOW
			},
			{
				from: 0.6,
				to: 0.85,
				color: ColorsConfig.CORRELATION_MATRIX_CATEGORY_COLOR.MODERATE
			},
			{
				from: 0.85,
				to: 0.95,
				color: ColorsConfig.CORRELATION_MATRIX_CATEGORY_COLOR.HIGH
			},
			{
				from: 0.95,
				color: ColorsConfig.CORRELATION_MATRIX_CATEGORY_COLOR.VERY_HIGH
			}
		],
		min: -1,
		max: 1,
		startOnTick: false,
		endOnTick: false
	},
	legend: {
		enabled: false
	},
	tooltip: {
		enabled: false,
		formatter: function () {
			return `<div class="margin-bottom-3 text-10" style="width:90px;">Fund Correlation</div>
			${StringUtil.escapeHtml(this.series.xAxis.categories[this.point.x]?.split('.')?.[0])}, 
			${StringUtil.escapeHtml(this.series.yAxis.categories[this.point.y]?.split('.')?.[0])}:
			<span class="text-12-bold">&nbsp;${StringUtil.escapeHtml(NumberUtil.numberFormatter(this.point.value))}</span>
			`;
		}
	}
};

export class HeatmapChartConfig extends DefaultChartConfig {
	constructor(
		protected type: ChartType,
		protected name: string,
		protected container: HTMLElement,
		protected data: HeatmapData,
		protected dataIndex?: number,
		protected colors?: Array<string>,
		protected height?: number,
		protected width?: number,
		protected title?: string,
		protected hideTitle?: boolean,
		protected x?: string,
		protected y?: string,
		protected customAttributes?: any
	) {
		super(type, container, colors, height, width, title, hideTitle);
		this.initialize();
	}

	private initialize(): void {
		this.configuration = ObjectUtil.extend(this.configuration, DEFAULT_HEATMAP_CONFIG);
		this.configuration.tooltip.enabled = this.customAttributes?.tooltip?.enabled;
		this.assignConfigProperties();
	}

	public render(): void {
		Highcharts.chart(this.configuration);
	}

	public getConfigurationForPDF(): any {
		if (this.customAttributes.heatmapData) {
			this.data = this.customAttributes.heatmapData;

			this.configuration = ObjectUtil.extend(this.configuration, {
				chart: {
					width: 510
				},
				xAxis: {
					labels: {
						rotation: 270,
						style: {
							fontSize: '7px'
						}
					}
				},
				yAxis: {
					labels: {
						style: {
							fontSize: '7px'
						}
					}
				}
			});
			this.assignConfigProperties();
		}

		return this.configuration;
	}

	private assignConfigProperties(): void {
		this.configuration.chart.type = ChartType.Heatmap;
		this.configuration.series[0].data = this.data.matrix;
		this.configuration.xAxis.categories = this.data.xLabels;
		this.configuration.yAxis.categories = this.data.yLabels;
	}
}
